import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../firebase';

/**
 * TaskService - CRUD for assigned tasks between staff members.
 *
 * Tasks are stored at schools/{schoolId}/tasks/{taskId}.
 * Any staff can assign tasks to any staff. School-readable.
 * Query pattern: array-contains on assignedTo for "my tasks".
 */
class TaskService {
  constructor() {
    this.cache = new Map();
    this.timestamps = new Map();
    this.ttl = 60 * 1000; // 1 minute
  }

  _cacheKey(schoolId, userId) {
    return `${schoolId}-${userId}`;
  }

  _collectionRef(schoolId) {
    return collection(db, 'schools', schoolId, 'tasks');
  }

  /**
   * Get all tasks assigned to a user (via array-contains).
   */
  async getTasksForUser(schoolId, userId, forceRefresh = false) {
    if (!schoolId || !userId) throw new Error('schoolId and userId are required');

    const key = this._cacheKey(schoolId, userId);
    const now = Date.now();

    if (!forceRefresh && this.cache.has(key) && (now - this.timestamps.get(key)) < this.ttl) {
      return this.cache.get(key);
    }

    try {
      const q = query(
        this._collectionRef(schoolId),
        where('assignedTo', 'array-contains', userId)
      );
      const snapshot = await getDocs(q);

      const tasks = snapshot.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          // Sort: pending first, then by dueDate ascending
          const statusOrder = { pending: 0, 'in-progress': 1, completed: 2 };
          const aDiff = (statusOrder[a.status] ?? 1) - (statusOrder[b.status] ?? 1);
          if (aDiff !== 0) return aDiff;
          const aTime = a.dueDate?.toMillis?.() || Infinity;
          const bTime = b.dueDate?.toMillis?.() || Infinity;
          return aTime - bTime;
        });

      this.cache.set(key, tasks);
      this.timestamps.set(key, now);
      return tasks;
    } catch (error) {
      console.error('[TaskService] Error fetching tasks:', error);
      throw error;
    }
  }

  /**
   * Get all tasks created by a user.
   */
  async getTasksCreatedBy(schoolId, userId, forceRefresh = false) {
    if (!schoolId || !userId) throw new Error('schoolId and userId are required');

    const key = `${schoolId}-created-${userId}`;
    const now = Date.now();

    if (!forceRefresh && this.cache.has(key) && (now - this.timestamps.get(key)) < this.ttl) {
      return this.cache.get(key);
    }

    try {
      const q = query(
        this._collectionRef(schoolId),
        where('assignedBy', '==', userId)
      );
      const snapshot = await getDocs(q);

      const tasks = snapshot.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || 0;
          const bTime = b.createdAt?.toMillis?.() || 0;
          return bTime - aTime;
        });

      this.cache.set(key, tasks);
      this.timestamps.set(key, now);
      return tasks;
    } catch (error) {
      console.error('[TaskService] Error fetching created tasks:', error);
      throw error;
    }
  }

  /**
   * Create a new task.
   */
  async createTask(schoolId, taskData, assignedByUserId) {
    if (!schoolId) throw new Error('schoolId is required');
    if (!taskData.title?.trim()) throw new Error('Task title is required');
    if (!taskData.assignedTo?.length) throw new Error('At least one assignee is required');

    const toTimestamp = (val) => {
      if (!val) return null;
      if (val instanceof Timestamp) return val;
      if (val instanceof Date) return Timestamp.fromDate(val);
      return Timestamp.fromDate(new Date(val));
    };

    const task = {
      title: taskData.title.trim(),
      description: taskData.description?.trim() || '',
      status: 'pending',
      priority: taskData.priority || 'normal',
      dueDate: toTimestamp(taskData.dueDate),
      assignedTo: taskData.assignedTo,
      assignedToNames: taskData.assignedToNames || {},
      assignedBy: assignedByUserId,
      assignedByName: taskData.assignedByName || '',
      relatedStudentId: taskData.relatedStudentId || null,
      relatedStudentName: taskData.relatedStudentName || null,
      relatedMentionId: taskData.relatedMentionId || null,
      relatedWorkflowInstanceId: taskData.relatedWorkflowInstanceId || null,
      relatedWorkflowStepId: taskData.relatedWorkflowStepId || null,
      completedAt: null,
      schoolId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    try {
      const ref = await addDoc(this._collectionRef(schoolId), task);
      this._invalidateCache(schoolId);
      return { id: ref.id, ...task };
    } catch (error) {
      console.error('[TaskService] Error creating task:', error);
      throw error;
    }
  }

  /**
   * Update task status (pending → in-progress → completed).
   */
  async updateStatus(schoolId, taskId, status) {
    if (!schoolId || !taskId) throw new Error('schoolId and taskId are required');
    if (!['pending', 'in-progress', 'completed'].includes(status)) {
      throw new Error('Invalid status');
    }

    const updates = {
      status,
      updatedAt: serverTimestamp(),
    };

    if (status === 'completed') {
      updates.completedAt = serverTimestamp();
    } else {
      updates.completedAt = null;
    }

    try {
      const docRef = doc(db, 'schools', schoolId, 'tasks', taskId);
      await updateDoc(docRef, updates);
      this._invalidateCache(schoolId);
      return { id: taskId, ...updates };
    } catch (error) {
      console.error('[TaskService] Error updating task status:', error);
      throw error;
    }
  }

  /**
   * Update task details.
   */
  async updateTask(schoolId, taskId, updates) {
    if (!schoolId || !taskId) throw new Error('schoolId and taskId are required');

    const toTimestamp = (val) => {
      if (!val) return null;
      if (val instanceof Timestamp) return val;
      if (val instanceof Date) return Timestamp.fromDate(val);
      return Timestamp.fromDate(new Date(val));
    };

    const data = { ...updates, updatedAt: serverTimestamp() };
    if (data.dueDate !== undefined) data.dueDate = toTimestamp(data.dueDate);

    try {
      const docRef = doc(db, 'schools', schoolId, 'tasks', taskId);
      await updateDoc(docRef, data);
      this._invalidateCache(schoolId);
      return { id: taskId, ...data };
    } catch (error) {
      console.error('[TaskService] Error updating task:', error);
      throw error;
    }
  }

  /**
   * Delete a task (hard delete).
   */
  async deleteTask(schoolId, taskId) {
    if (!schoolId || !taskId) throw new Error('schoolId and taskId are required');

    try {
      const docRef = doc(db, 'schools', schoolId, 'tasks', taskId);
      await deleteDoc(docRef);
      this._invalidateCache(schoolId);
    } catch (error) {
      console.error('[TaskService] Error deleting task:', error);
      throw error;
    }
  }

  _invalidateCache(schoolId) {
    for (const key of this.cache.keys()) {
      if (key.startsWith(schoolId)) {
        this.cache.delete(key);
        this.timestamps.delete(key);
      }
    }
  }
}

export const taskService = new TaskService();
export default taskService;
